import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOSchema from '@/components/feature/SEOSchema';
import { categories, allArticles } from '@/mocks/data';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 underline">
              返回首页
            </Link>
          </div>
        </main>
        <Footer newsletterSubmitAddr="https://readdy.ai/api/form/d9st0fllbrt1qcgaj9n0" />
      </>
    );
  }

  const categoryArticles = allArticles.filter((a) => a.category === slug);

  return (
    <>
      <ScrollToTop />
      <SEOSchema
        type="WebPage"
        data={{
          name: `${category.name} - 乳色吐息动漫`,
          description: category.description,
        }}
      />

      <Navbar />
      <main className="pt-16">
        <section className="relative w-full h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden">
          <img
            src={category.image}
            alt={category.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
          <div className="relative z-10 text-center px-4">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm mb-4">
              <i className={`${category.icon} text-white text-2xl`}></i>
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-2">{category.name}</h1>
            <p className="text-sm md:text-base text-white/70 max-w-md mx-auto">{category.description}</p>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { name: '首页', path: '/' },
                  { name: category.name },
                ]}
              />
            </div>

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm transition-all duration-200 whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-200/70"
                >
                  <div className="aspect-[3/2] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                      <span>{article.date}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span>{article.readTime}</span>
                    </div>
                    <h3 className="text-base font-heading font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
              探索更多内容
            </h2>
            <p className="text-sm text-foreground-500 mb-6 max-w-md mx-auto">
              还有更多精彩分类等待您的探索
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {categories
                .filter((c) => c.slug !== slug)
                .slice(0, 4)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="px-5 py-2.5 rounded-full bg-background-50 text-sm text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-all duration-200 whitespace-nowrap"
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer newsletterSubmitAddr="https://readdy.ai/api/form/d9st0fllbrt1qcgaj9n0" />
    </>
  );
}